'use client';

import { forwardRef, useState, type TextareaHTMLAttributes, type CSSProperties } from 'react';
import { SField, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { type SIconName } from '../SIcon';
import { cn } from '../../lib/cn';
import { AUTOFILL_RESET_CLASS } from '../../lib/autofill';
import { type SColor } from '../../lib/color';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export interface STextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> {
  /** 값 (제어) */
  value?: string;
  /** 값 변경 (sdUpdate) */
  onValueChange?: (value: string) => void;
  /** 네이티브 onChange (form-agnostic) */
  onChange?: TextareaHTMLAttributes<HTMLTextAreaElement>['onChange'];
  /** 행 수 */
  rows?: number;
  /** 유효성 규칙 — blur 시 자동 검증 */
  rules?: Rule[];
  /** 필드 상태 ('default' | 'pass' | 'error') */
  status?: SFieldStatus;
  /** 포커스 상태 (제어/반영) */
  focused?: boolean;
  /** 호버 상태 (제어/반영) */
  hovered?: boolean;
  /** 내부 textarea 요소 className */
  textareaClass?: string;
  /** 내부 textarea 요소 style */
  textareaStyle?: CSSProperties;
  // ── SField 래퍼 ──
  label?: string;
  labelWidth?: number | string;
  /** 레이블 영역 아이콘 */
  icon?: SIconName;
  iconColor?: SColor;
  /** 레이블 툴팁 텍스트 */
  labelTooltip?: string;
  /** 레이블 툴팁 상세 옵션 */
  labelTooltipProps?: Partial<STooltipProps>;
  /** 우측 어드온 레이블 */
  addonLabel?: string;
  /** 어드온 정렬 */
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  width?: number | string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

// sd-textarea는 단일 사이즈 (원본에 size prop 없음)
const PAD = { fontSize: 12, paddingX: 12, paddingY: 4 };

/** STextarea — sd-textarea 포팅. SField(멀티라인 테두리 박스) + borderless native textarea. */
export const STextarea = forwardRef<HTMLTextAreaElement, STextareaProps>(function STextarea(
  {
    value,
    onValueChange,
    onChange,
    rows = 3,
    rules,
    status,
    focused,
    hovered,
    textareaClass,
    textareaStyle,
    label,
    labelWidth,
    icon,
    iconColor,
    labelTooltip,
    labelTooltipProps,
    addonLabel,
    addonAlign,
    hint,
    error,
    errorMessage,
    width,
    disabled = false,
    readOnly = false,
    placeholder = '입력해 주세요.',
    onBlur,
    className,
    style,
    ...rest
  },
  ref,
) {
  const p = PAD;
  const [ruleError, setRuleError] = useState('');

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name: rest.name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedErrorMessage = ruleError !== '' ? ruleError : errorMessage;
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused}
      hovered={hovered}
      errorMessage={resolvedErrorMessage}
      width={width}
      disabled={disabled}
      readOnly={readOnly}
      multiline
      className={className}
      style={style}
    >
      <textarea
        ref={ref}
        // onChange 가 항상 부착되므로 value 를 항상 지정해 controlled 로 고정한다.
        value={value ?? ''}
        rows={rows}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={e => {
          onChange?.(e);
          onValueChange?.(e.target.value);
          if (ruleError !== '') setRuleError(runRules(e.target.value, rules));
        }}
        onBlur={e => {
          onBlur?.(e);
          if (rules && rules.length > 0) setRuleError(runRules(e.target.value, rules));
        }}
        className={cn(
          'w-full resize-y border-0 bg-transparent outline-none placeholder:text-[color:var(--sys-color-field-text-placeholder)] disabled:cursor-not-allowed',
          AUTOFILL_RESET_CLASS,
          textareaClass,
        )}
        style={{
          minHeight: 100,
          padding: `${p.paddingY}px ${p.paddingX}px`,
          fontSize: p.fontSize,
          lineHeight: '20px',
          color: disabled
            ? 'var(--cmp-textinput-text-disabled)'
            : 'var(--sys-color-field-text-default)',
          ...textareaStyle,
        }}
        {...rest}
      />
    </SField>
  );
});
